import React from 'react';
import {View, StyleSheet} from 'react-native';
import Styles from '../../../Styles';
import AVText from '../../../elements/AVText';
import AVFormattedCurrency from '../../../elements/AVFormattedCurrency';
import {SubsidyDetails} from 'src/types/SubsidyDetails';
import Localized from 'src/constants/AppStrings';
import {
  getSubsidyCardStyles,
  formatSubsidyDate,
} from 'src/utils/subsidyCardUtils';
import {SubsidyLogo} from './SubsidyLogo';

interface SubsidyCardProps {
  subsidyDetails: SubsidyDetails;
}

export const SubsidyCard: React.FC<SubsidyCardProps> = ({subsidyDetails}) => {
  const {cardColors, textColor, logoSize} = getSubsidyCardStyles();
  const styles = createStyles(cardColors, textColor);

  return (
    <View style={styles.card} accessible={true}>
      <View style={styles.cardContent}>
        <View style={styles.leftColumn}>
          <View style={styles.balanceSection}>
            <AVText
              style={styles.balanceLabel}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm6}
              accessible={true}
              accessibilityLabel={Localized.Labels.subsidy_balance}
            >
              {Localized.Labels.subsidy_balance}
            </AVText>
            <AVFormattedCurrency
              value={subsidyDetails.balance}
              currency={subsidyDetails.currency}
              style={styles.balanceAmount}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
              accessible={true}
              accessibilityLabel={`${subsidyDetails.balance} ${subsidyDetails.currency}`}
            />
          </View>

          <View style={styles.spacer} />

          <View style={styles.nameSection}>
            <AVText
              style={styles.holderName}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm6}
              accessible={true}
              accessibilityLabel={`Subsidy holder: ${subsidyDetails.holderName}`}
            >
              {subsidyDetails.holderName}
            </AVText>
          </View>
        </View>

        <View style={styles.rightColumn}>
          <View style={styles.logoSection}>
            <SubsidyLogo logoUrl={subsidyDetails.logoUrl} size={logoSize} />
          </View>

          <View style={styles.spacer} />

          {subsidyDetails.endDate && (
            <View style={styles.dateSection}>
              <AVText
                style={styles.dateLabel}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
                accessible={true}
                accessibilityLabel={Localized.Labels.end_date}
              >
                {Localized.Labels.end_date}
              </AVText>
              <AVText
                style={styles.dateValue}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm6}
                accessible={true}
                accessibilityLabel={`End date: ${formatSubsidyDate(
                  subsidyDetails.endDate,
                )}`}
              >
                {formatSubsidyDate(subsidyDetails.endDate)}
              </AVText>
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

const createStyles = (
  cardColors: {backgroundColor: string; borderColor: string},
  textColor: string,
) => {
  return StyleSheet.create({
    card: {
      backgroundColor: cardColors.backgroundColor,
      borderWidth: 2,
      borderStyle: 'solid',
      borderColor: cardColors.borderColor,
      borderBottomWidth: 2,
      borderRadius: 20,
      padding: Styles.Spacing.m3,
      marginHorizontal: Styles.Spacing.m3,
      marginTop: Styles.Spacing.m3,
      marginBottom: 0,
      minHeight: 180,
      overflow: 'visible',
    },

    cardContent: {
      flex: 1,
      flexDirection: 'row',
      justifyContent: 'space-between',
    },

    leftColumn: {
      flex: 1,
      justifyContent: 'space-between',
    },

    rightColumn: {
      alignItems: 'flex-end',
      justifyContent: 'space-between',
    },

    spacer: {
      flex: 1,
    },
    balanceSection: {
      alignItems: 'flex-start',
    },
    balanceLabel: {
      fontSize: 18,
      fontWeight: '400',
      color: textColor,
      fontFamily: Styles.FontFamily.aeonikRegular,
      marginBottom: 4,
    },
    balanceAmount: {
      fontSize: 24,
      fontWeight: '700',
      color: textColor,
      fontFamily: Styles.FontFamily.aeonikRegular,
    },
    logoSection: {
      alignItems: 'flex-end',
    },

    nameSection: {
      alignItems: 'flex-start',
    },
    holderName: {
      fontSize: 18,
      fontWeight: '400',
      color: textColor,
      fontFamily: Styles.FontFamily.aeonikRegular,
    },

    dateSection: {
      alignItems: 'flex-end',
    },
    dateLabel: {
      fontSize: 12,
      fontWeight: '400',
      color: textColor,
      fontFamily: Styles.FontFamily.aeonikRegular,
      marginBottom: 2,
    },
    dateValue: {
      fontSize: Styles.Fonts.f0,
      fontWeight: '700',
      color: textColor,
      fontFamily: Styles.FontFamily.aeonikRegular,
    },
  });
};
